import type {
	TransferRecordOutput,
	TransferRecordState,
} from "@/api/custom_types";
import { useApiError, useTranslation } from "@/hooks";
import { useFacets } from "@/hooks/useFacetFilters";
import { Route } from "@/routes/st-admin/transactions";
import {
	TRANSFER_RECORDS_FACET_SOURCE,
	useGetTransferRecords,
	useGetTransferRecordsPlayerFacets,
} from "@/services";
import { cleanQuery } from "@/utils/queryClient";
import { getTimeSinceText } from "@/utils/timestamp";
import { calculateTaxPercentage, formatNumber } from "@/utils/tools";
import {
	type StatusTranslation,
	getTransferRecordStatusTranslation,
} from "@/utils/translations";
import {
	Chip,
	type ChipVariant,
	DataTable,
	Typography,
	useServerTableState,
} from "@st-marketplace/ui";
import { Link, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { useCallback, useMemo } from "react";
import { type FilterFormData, TransactionsFilter } from "./components";

const STATUS_COLOR_TO_CHIP_VARIANT: Record<
	StatusTranslation["color"],
	ChipVariant
> = {
	success: "success",
	primary: "info",
	error: "error",
	warning: "warning",
};

const ms = (value: number | null) => (value === null ? "-" : `${value} ms`);

const Transactions = () => {
	const { t, lang } = useTranslation();
	const { getErrorInterface } = useApiError();
	const navigate = useNavigate({ from: "/st-admin/transactions" });
	const search = Route.useSearch();

	const server = useServerTableState();

	const filters: FilterFormData = useMemo(
		() => ({
			orderEmail: search.orderEmail ?? "",
			senderEmail: search.senderEmail ?? "",
			states: search.states ?? [],
			players: search.players ?? [],
		}),
		[search],
	);
	const handleSearch = useCallback(
		(values: FilterFormData) => {
			server.onPageChange(0);
			navigate({
				search: (prev) => ({
					...prev,
					orderEmail: values.orderEmail.trim(),
					senderEmail: values.senderEmail.trim(),
					states: values.states,
					players: values.players,
				}),
				replace: true,
			});
		},
		[navigate, server],
	);

	const filterQuery = cleanQuery({
		order_email: search.orderEmail || undefined,
		sender_email: search.senderEmail || undefined,
		states: search.states.length
			? (search.states as TransferRecordState[])
			: undefined,
		resource_ids: search.players.length
			? search.players.map(Number).filter(Number.isFinite)
			: undefined,
	});

	const {
		data: transactions,
		isLoading,
		isFetching,
		error,
		refetch,
	} = useGetTransferRecords({
		query: {
			page: server.pageIndex + 1,
			page_size: server.pageSize,
			...filterQuery,
		},
	});

	const facetCounts = useFacets({
		source: TRANSFER_RECORDS_FACET_SOURCE,
		filters: filterQuery,
		ownParams: { statuses: "states" },
	});
	const { data: players } = useGetTransferRecordsPlayerFacets({
		query: filterQuery,
	});

	const rows = transactions?.results ?? [];
	const rowCount = transactions?.count ?? rows.length;

	const columns = useMemo<ColumnDef<TransferRecordOutput>[]>(
		() => [
			{
				id: "player_info",
				header: t("transactions.tablePlayer"),
				enableSorting: false,
				cell: ({ row }) => {
					const player = row.original.transfer_player_with_config;
					if (!player) return "-";
					const name = player.nick_name || player.full_name || "-";
					const rating =
						player.rating !== undefined && player.rating !== null
							? player.rating
							: "-";
					const rarity = player.rarity_name || "-";
					return (
						<Typography variant="body-sm" className="font-semibold">
							{`${name} ${rating} ${rarity}`}
						</Typography>
					);
				},
			},
			{
				id: "sender_email",
				header: t("transactions.tableSenderEmail"),
				enableSorting: false,
				cell: ({ row }) => {
					const { sender_id, sender_email } = row.original;
					if (!sender_id) return sender_email;
					return (
						<Link
							to="/senders/$senderId"
							params={{ senderId: sender_id }}
							className="text-primary hover:underline"
						>
							{sender_email}
						</Link>
					);
				},
			},
			{
				id: "order_email",
				header: t("transactions.tableOrderEmail"),
				enableSorting: false,
				cell: ({ row }) => (
					<Link
						to="/orders/$orderId"
						params={{ orderId: row.original.order_id }}
						className="text-primary hover:underline"
					>
						{row.original.order_email}
					</Link>
				),
			},
			{
				id: "taken",
				header: t("transactions.tableTaken"),
				enableSorting: false,
				cell: ({ row }) => formatNumber(row.original.taken),
			},
			{
				id: "transferred",
				header: t("transactions.tableTransferred"),
				enableSorting: false,
				cell: ({ row }) => formatNumber(row.original.transferred),
			},
			{
				id: "tax",
				header: t("transactions.tableTax"),
				enableSorting: false,
				cell: ({ row }) => {
					const { taken, transferred } = row.original;
					const tax = calculateTaxPercentage(transferred, taken);

					return formatNumber(tax, "percent");
				},
			},
			{
				id: "buy_speed",
				header: t("admin.dashboard.buySpeed"),
				enableSorting: false,
				cell: ({ row }) => ms(row.original.snipe_speed_ms),
			},
			{
				id: "search_speed",
				header: t("admin.dashboard.searchSpeed"),
				enableSorting: false,
				cell: ({ row }) => ms(row.original.search_speed_ms),
			},
			{
				id: "list_speed",
				header: t("admin.dashboard.listSpeed"),
				enableSorting: false,
				cell: ({ row }) => ms(row.original.sell_speed_ms),
			},
			{
				id: "state",
				header: t("transactions.tableStatus"),
				enableSorting: false,
				cell: ({ row }) => {
					const { color, text } = getTransferRecordStatusTranslation(
						row.original.state,
						t,
					);
					return (
						<Chip variant={STATUS_COLOR_TO_CHIP_VARIANT[color]} size="sm">
							{text}
						</Chip>
					);
				},
			},
			{
				id: "created_at",
				header: t("transactions.tableDate"),
				enableSorting: false,
				cell: ({ row }) =>
					(row.original.created_at &&
						getTimeSinceText(row.original.created_at, t, lang)) ||
					"-",
			},
		],
		[t, lang],
	);

	return (
		<div className="flex flex-col gap-5">
			<Typography variant="title-md" as="h1">
				{t("transactions.title")}
			</Typography>

			<TransactionsFilter
				onSearch={handleSearch}
				defaultValues={filters}
				facetCounts={facetCounts}
				players={players}
			/>

			<DataTable
				columns={columns}
				data={rows}
				totalItems={rowCount}
				server={server}
				isLoading={isLoading}
				isFetching={isFetching}
				error={error ? getErrorInterface(error) : null}
				onRetry={refetch}
				getRowId={(row) => row.id}
			/>
		</div>
	);
};

export default Transactions;
